/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import Layout, { LayoutProps } from 'components/layout';
import { useFormik, FormikErrors } from 'formik';
import config from '../../website-config';
import DOMPurify from 'isomorphic-dompurify';
import { Helmet } from 'react-helmet';
import { ConsentDetails } from '../../models/consent';
import { PDFDocument, StandardFonts } from 'pdf-lib';

const initial: ConsentDetails = {
    patientName: '',
    guardianName: '',
    relationship: '',
    dob: '',
};
async function createDoc(details: ConsentDetails) {
    const url1 = '/assets/consent-letter.pdf';

    const donorPdfBytes = await fetch(url1).then(res => res.arrayBuffer());

    const pdfDoc = await PDFDocument.load(donorPdfBytes);
    const font = await pdfDoc.embedFont(StandardFonts.TimesRoman);

    const pages = pdfDoc.getPages();
    const firstPage = pages[0];
    firstPage.drawText(details.patientName, {
        x: 230,
        y: 640,
        size: 11,
        font: font,
    });
    firstPage.drawText(details.dob, {
        x: 230,
        y: 618,
        size: 11,
        font: font,
    });
    firstPage.drawText(details.guardianName, {
        x: 230,
        y: 598,
        size: 11,
        font: font,
    });
    firstPage.drawText(details.relationship, {
        x: 230,
        y: 577,
        size: 11,
        font: font,
    });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    return window.open(url, '_blank');
}

const Consent = () => {
    const [status, setStatus] = useState('');

    const layoutProps: LayoutProps = {
        isHome: false,
        currentPage: 'consent',
    };

    const formik = useFormik<ConsentDetails>({
        initialValues: initial,
        onSubmit: values => {
            console.log(values);
            setStatus('submitted');
        },
        validate: values => {
            const errors: FormikErrors<ConsentDetails> = {};

            const australianDateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/;

            if (values.dob) {
                if (!australianDateRegex.test(values.dob)) {
                    errors.dob = 'Invalid data of birth (dd/mm/yyyy)';
                }
            }

            return errors;
        },
    });

    const { errors, touched, handleChange, values, resetForm } = formik;

    const createPdf = async () => {
        const cleanGuardianName = DOMPurify.sanitize(values.guardianName);
        const cleanPatientName = DOMPurify.sanitize(values.patientName);
        const cleanRelationship = DOMPurify.sanitize(values.relationship);
        const cleanDOB = DOMPurify.sanitize(values.dob);

        const newWin = await createDoc({
            patientName: cleanPatientName,
            guardianName: cleanGuardianName,
            relationship: cleanRelationship,
            dob: cleanDOB,
        });

        if (!newWin || newWin.closed || typeof newWin.closed == 'undefined') {
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (status === 'submitted') {
            setStatus('loading');
        }
        if (status === 'loading') {
            createPdf()
                .then(x => (x ? setStatus('ready') : setStatus('blocked')))
                .catch(_ => setStatus('blocked'));
            resetForm();
        }
    }, [status]);

    const isLoading = status === 'loading';
    const isReady = status === 'ready';
    const isError = status === 'error';
    const isBlocked = status === 'blocked';

    return (
        <div>
            <Helmet>
                <title>Calvary Paediatrics - Generate Consent Form</title>
                <meta name="description" content={config.description} />
                <link rel="canonical" href={`http://calvarypaediatrics.com.au/appointment`} />
            </Helmet>
            <Layout {...layoutProps}>
                <section id="consent" className="contact">
                    <div className="container">
                        <div className="page-title">
                            <h2>Informed Consent for Paediatric Physical Examination</h2>
                            <br />
                            <span>
                                Create an &quot;Including Genital/Perianal and Breast Examination - Including
                                Genital/Perianal and Breast Examination&quot; form to bring to your appointment.
                            </span>
                            <br />
                            <span>
                                You can optionally prefill the form using the fields below or just click Generate
                                Consent Form button.
                            </span>
                        </div>
                        <div className="col-lg-12 mt-5 mt-lg-0">
                            <form onSubmit={formik.handleSubmit} className="php-email-form">
                                <div className="form-group">
                                    <label htmlFor="patientName">Patient Name</label>
                                    <input
                                        type="text"
                                        name="patientName"
                                        className="form-control"
                                        id="patientName"
                                        placeholder="Enter name of patient"
                                        onChange={handleChange}
                                        value={values.patientName}
                                    />
                                    <div className="validate">
                                        {errors.patientName && touched.patientName ? (
                                            <div>{errors.patientName}</div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="guardianName">Parent/Guardian Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="guardianName"
                                        id="guardianName"
                                        placeholder="Enter name of parent or guardian"
                                        onChange={handleChange}
                                        value={formik.values.guardianName}
                                    />
                                    <div className="validate">
                                        {errors.guardianName && touched.guardianName ? (
                                            <div>{errors.guardianName}</div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="col-md-3 pl-0 form-group">
                                    <label htmlFor="guardianName">Date of Birth</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="dob"
                                        id="dob"
                                        placeholder="Enter date of birth (dd/mm/yyyy)"
                                        onChange={handleChange}
                                        value={formik.values.dob}
                                    />
                                    <div className="validate">
                                        {errors.dob && touched.dob ? <div>{errors.dob}</div> : null}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="guardianName">Relationship to Child</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="relationship"
                                        id="relationship"
                                        placeholder="Enter relationship to child"
                                        onChange={handleChange}
                                        value={formik.values.relationship}
                                    />
                                    <div className="validate">
                                        {errors.relationship && touched.relationship ? (
                                            <div>{errors.relationship}</div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    {isLoading && <div className="loading">Loading</div>}
                                    {isReady && (
                                        <div className="sent-message">Your form has been created. Thank you!</div>
                                    )}
                                    {isError && (
                                        <div className="error-message">
                                            Problem sending your request, please try again.
                                        </div>
                                    )}
                                </div>
                                <div className="text-center">
                                    <button type="submit">Generate/View Consent Form</button>
                                </div>
                            </form>
                            {isBlocked && (
                                <div className="error-message">
                                    <div className="alert alert-secondary" role="alert">
                                        <span>
                                            Consent form may be blocked on your device. Click{' '}
                                            <a href="/assets/consent-letter.pdf" download>
                                                here
                                            </a>{' '}
                                            to download a copy.
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </Layout>
        </div>
    );
};

export default Consent;
